import { useState } from "react";
import type { z } from "zod";

/**
 * How a billing form tells an employee what is wrong (ADR-0073).
 *
 * Every request is parsed through the shared schema before it leaves the browser, so the server
 * never sees a shape it refuses. When that parse fails the employee needs to know which field and
 * what it wants — not a Zod issue list. This module turns issues into one message per field and
 * gives the forms one hook that holds those messages, remembers that an attempt failed (so every
 * field can show its requirement), and clears a field's message the moment it is edited.
 */

/** One message per field, keyed by the field's name: the form's own, or the schema's. */
export type FieldErrors = Record<string, string>;

/** Every billing write asks for one; the same words everywhere it is asked. */
export const REASON_REQUIREMENT =
  "3 to 500 characters. History and the audit trail show it to colleagues.";

/** Stands in for an issue that says nothing a person could act on; the form swaps in the requirement. */
export const CHECK_THIS_FIELD = "Check this field.";

export class BillingFormError extends Error {
  readonly fields: FieldErrors;

  constructor(fields: FieldErrors) {
    const messages = Object.values(fields);
    super(messages.length === 1 ? (messages[0] ?? "") : "Check the highlighted fields.");
    this.name = "BillingFormError";
    this.fields = fields;
  }
}

type Issue = z.core.$ZodIssue;

function sized(issue: Issue): { origin?: string; minimum?: number; maximum?: number } {
  const detail = issue as { origin?: string; minimum?: number | bigint; maximum?: number | bigint };
  return {
    ...(detail.origin === undefined ? {} : { origin: detail.origin }),
    ...(detail.minimum === undefined ? {} : { minimum: Number(detail.minimum) }),
    ...(detail.maximum === undefined ? {} : { maximum: Number(detail.maximum) }),
  };
}

function tooSmall(issue: Issue): string {
  const { origin, minimum = 0 } = sized(issue);
  const blank = typeof issue.input === "string" && issue.input.trim() === "";
  if (origin === "string") {
    return minimum <= 1 || blank ? "Required." : `At least ${minimum} characters.`;
  }
  if (origin === "array") return `Add at least ${minimum}.`;
  return `Must be ${minimum} or more.`;
}

function tooBig(issue: Issue): string {
  const { origin, maximum = 0 } = sized(issue);
  if (origin === "string") return `At most ${maximum} characters.`;
  if (origin === "array") return `No more than ${maximum}.`;
  return `Must be ${maximum} or less.`;
}

/**
 * A message an employee can act on. A type or format failure gets the placeholder, because "expected
 * number, received NaN" helps nobody; custom issues keep their message, since the schema authors
 * wrote those for exactly this reader.
 */
export function issueMessage(issue: Issue): string {
  switch (issue.code) {
    case "too_small":
      return tooSmall(issue);
    case "too_big":
      return tooBig(issue);
    case "custom":
      return issue.message;
    case "invalid_format":
      return issue.format === "email" ? "Enter a valid email address." : CHECK_THIS_FIELD;
    default:
      return CHECK_THIS_FIELD;
  }
}

/** Field messages from a failed parse, keyed by the schema path ("lines.0.description"). */
export function fieldErrorsFrom(error: z.ZodError): FieldErrors {
  const fields: FieldErrors = {};
  for (const issue of error.issues) {
    const field = issue.path.length === 0 ? "form" : issue.path.map(String).join(".");
    if (!(field in fields)) fields[field] = issueMessage(issue);
  }
  return fields;
}

/** Parse through the shared schema, or throw what the form should show. */
export function validated<TSchema extends z.ZodType>(
  schema: TSchema,
  input: unknown,
): z.output<TSchema> {
  const parsed = schema.safeParse(input, { reportInput: true });
  if (parsed.success) return parsed.data;
  throw new BillingFormError(fieldErrorsFrom(parsed.error));
}

export type FieldGuide = {
  /** The form's name for a schema field, when they differ ("amountDueMinor" → "amount"). */
  rename?: (schemaField: string) => string;
  /** What each field requires, in the employee's words. Shown after a failed attempt. */
  requirements: Record<string, string>;
};

export type FormErrors = {
  /** Per-field messages from the last failed attempt, keyed by the form's field names. */
  fields: FieldErrors;
  /** Whether any attempt has failed, which is when every field shows its requirement. */
  attempted: boolean;
  /** The message for the form as a whole: the server's refusal, or nothing. */
  summary: string | null;
  /** Record a failure: a field breakdown when there is one, the message otherwise. */
  fail: (error: unknown) => void;
  /** The employee edited a field; its message no longer applies. */
  clear: (field: string) => void;
  reset: () => void;
};

function translate(fields: FieldErrors, guide: FieldGuide): FieldErrors {
  const translated: FieldErrors = {};
  for (const [schemaField, message] of Object.entries(fields)) {
    const field = guide.rename?.(schemaField) ?? schemaField;
    if (field in translated) continue;
    translated[field] =
      message === CHECK_THIS_FIELD ? (guide.requirements[field] ?? message) : message;
  }
  return translated;
}

export function useFormErrors(guide: FieldGuide): FormErrors {
  const [fields, setFields] = useState<FieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  return {
    fields,
    attempted,
    summary,
    fail: (error) => {
      setAttempted(true);
      if (error instanceof BillingFormError) {
        const { form, ...rest } = translate(error.fields, guide);
        setFields(rest);
        setSummary(form ?? null);
        return;
      }
      setFields({});
      setSummary(error instanceof Error ? error.message : "Something went wrong.");
    },
    clear: (field) => {
      setFields((current) =>
        field in current
          ? Object.fromEntries(Object.entries(current).filter(([name]) => name !== field))
          : current,
      );
    },
    reset: () => {
      setFields({});
      setSummary(null);
      setAttempted(false);
    },
  };
}

/** What a field on screen carries besides its value: its requirement, its error, and whether to show the requirement. */
export type FieldState = {
  id: string;
  hint?: string;
  error?: string;
  showHint: boolean;
};

/** The requirement and error state of one field, read from the form's errors and guide. */
export function fieldState(
  errors: FormErrors,
  requirements: Record<string, string>,
  name: string,
  id = name,
): FieldState {
  const hint = requirements[name];
  const error = errors.fields[name];
  return {
    id,
    ...(hint === undefined ? {} : { hint }),
    ...(error === undefined ? {} : { error }),
    showHint: errors.attempted,
  };
}
